<template>
	<div class="container">
		<a-dialog class="survey-results, divided">
			<h1>{{ $t("titleReport") }}</h1>
			<i18n v-if="results.first_name" path="intro" tag="p">
				<span place="first_name">{{ results.first_name }}</span>
				<span place="last_name">{{ results.last_name }}</span>
				<span place="company_name">{{ results.company_name }}</span>
			</i18n>
			<div class="survey-results__split">
				<div class="survey-results__spider">
					<h2>{{ $t("subtitleReport") }}</h2>
					<results-chart
						:categories="scores"
						class="survey-results__graph"
					/>
				</div>
				<div class="survey-results__totals">
					<h2>{{ $t("scores") }}</h2>
					<results-bar :scores="scores" />
				</div>
			</div>
			<hr class="divider"/>
		</a-dialog>
		<a-dialog v-if="recommendations.length > 0" class="survey-results, divided">
			<h1>{{ $t("titleRecommendations") }}</h1>
			<div
				v-for="(category, index) in recommendations"
				:key="index"
				class="survey-results__recommendation-section"
			>
				<h2 class="survey-results__alt-title">{{ category.name }}</h2>
				<item-list
					:items="category.values"
					icon-color="secondary"
					icon="lens"
					icon-size="11"
				/>
			</div>
			<hr class="divider"/>
		</a-dialog>
		<a-dialog class="survey-results">
			<div class="survey-results__split">
				<div
					class="survey-results__pane survey-results__pane--big"
					v-html="$t('resultsLeft')"
				></div>
				<div class="survey-results__pane">
					<div v-html="$t('resultsRight')"></div>
					<a-button
						:to="{ name: 'ResultsInvoice', params: { uuid } }"
						class="survey-results__contact-me"
						>{{ $t("contact") }}</a-button
					>
					<p class="survey-results__paragraph--small">
						{{ $t("disclaimer") }}
					</p>
				</div>
			</div>
		</a-dialog>
	</div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import ADialog from "@/components/Dialog";
import AButton from "@/components/Button";
import ResultsChart from "@/components/ResultsChart";
import ResultsBar from "@/components/ResultsBar";
import ItemList from "@/components/ItemList";
import i18n from "@/plugins/i18n";
import survey from "@/survey";

export default {
	components: {
		ADialog,
		AButton,
		ResultsChart,
		ResultsBar,
		ItemList
	},
	props: {
		uuid: {
			type: String,
			required: true
		}
	},
	computed: {
		...mapGetters("surveyResults", ["results"]),
		benefits() {
			return this.$t("benefits");
		},
		locale() {
			return i18n.locale;
		},
		scores() {
			return this.results.scores;
		},
		recommendations() {
			return Object.entries(this.results.recommendations).map(
				([categoryId, values]) => ({
					name: survey.getCategoryById(categoryId).name[this.locale],
					values: values.map(
						recommendation => recommendation[this.locale]
					)
				})
			);
		}
	},
	watch: {
		uuid() {
			this.fetchData(this.uuid);
		}
	},
	i18n: {
		messages: {
			en: {
				titleReport: "Full report of your maturity scan",
				titleUnlock: "Unlocked value after implementation",
				intro:
					"Dear {first_name} {last_name}, here are the results of the maturity scan for {company_name}.",
				subtitleReport: "Overview",
				scores: "Scores",
				titleRecommendations: "Recommendations",
				contact: "Contact me",
				disclaimer: `* Recommendations and improvements are based on the
						maturity level data from the survey. We assume that all answers
						are applicable for your organisation.`
			},
			nl: {
				titleReport: "Volledig rapport van je volwassenheidsscan",
				contact: "Neem contact op",
				titleUnlock: "Ontsloten data na implementatie",
				intro:
					"Beste {first_name} {last_name}, hier zijn de resultaten van de volwassenheidsmeting voor {company_name}.",
				subtitleReport: "Overzicht",
				scores: "Scores",
				titleRecommendations: "Aanbevelingen",
				disclaimer:
					"* Aanbevelingen en verbeteringen zijn gebaseerd op de ingevulde data in de enquête. We gaan er van uit dat alle antwoorden van toepassing zijn op je organisatie."
			}
		}
	},
	created() {
		this.fetchData(this.uuid);
	},
	methods: {
		...mapActions("surveyResults", ["fetchData"])
	},
	beforeRouteEnter(to, from, next) {
		survey.waitForResponse(next);
	}
};
</script>

<style lang="scss">
.survey-results__split {
	display: flex;
}
.survey-results__spider,
.survey-results__totals {
	flex: 0 1 50%;

	.results-bar__progress {
		margin-bottom: 25px;
	}
}
.survey-results__spider {
	padding-right: 32px;
}
.survey-results__spider .survey-results__sub-title {
	padding-bottom: 0;
}
.survey-results__sub-title {
	padding: 25px 0 15px;
}
.survey-results__alt-title {
	padding: 25px 0 0;
}
.survey-results__pane {
	flex: 1 0 40%;
}
.survey-results__pane--big {
	flex: 2 0 60%;
	padding: 0 20px 0 0;
}
.survey-results__recommendation-section:last-child {
	padding-bottom: 50px;
}
.survey-results__contact-me {
	margin: 15px 0;
}
.survey-results__paragraph--small {
	font-size: 12px;
	line-height: 2;
}
.survey-results__graph {
	margin-top: -35px;
	max-width: 500px;
}
.divider {
  border: 0;
  border-top: 1px solid #dadada;
  margin-top: 80px;
}
.divided {
  margin-bottom: 0px;
}
.divided > .dialog__body {
  padding-bottom: 0px;
}

@media only screen and (max-width: 867px) {
	.survey-results {
		box-shadow: none;
	}
	.survey-results__split {
		flex-direction: column;
	}
	.survey-results__recommendation-section:last-child {
		padding-bottom: 0;
	}
	.survey-results__spider {
		padding-right: 0;
	}
	.results-bar {
		margin-top: 40px;
	}
}
</style>
